import { useEffect, useState, useCallback } from "react";
import api, { extractError } from "@/lib/api";
import { SectionHeader } from "@/components/SectionHeader";
import { StatusPill } from "@/components/StatusPill";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Search, MoreHorizontal, PencilLine, Trash2, History } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatCompactINR, formatDate, formatNumber } from "@/lib/format";

export default function ClientsPage() {
  const { user } = useAuth();
  const canManage = user.role === "admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ client_name: "", client_code: "" });
  const [history, setHistory] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/clients", { params: q ? { q } : {} });
    setRows(data);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const startEdit = (c) => {
    setEditing(c);
    setEditForm({ client_name: c.client_name, client_code: c.client_code });
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/clients/${editing.id}`, editForm);
      toast.success("Client updated");
      setEditing(null); load();
    } catch (e) { toast.error(extractError(e)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete client "${c.client_name}" (${c.client_code})?`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Client deleted");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };
  const openHistory = async (c) => {
    try {
      const { data } = await api.get(`/clients/${c.id}/history`);
      setHistory(data);
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div data-testid="clients-page">
      <SectionHeader title="Clients" description="Client directory — hierarchy-scoped for non-admin roles." />
      <Card className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="relative mb-4 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or code…" className="pl-9" />
        </div>
        <div className="overflow-x-auto rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="label-caps">Client Code</TableHead>
                <TableHead className="label-caps">Client Name</TableHead>
                <TableHead className="label-caps">Onboarded</TableHead>
                {canManage && <TableHead className="w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.client_code}`} className="hover:bg-muted/40">
                  <TableCell className="font-mono text-sm">{c.client_code}</TableCell>
                  <TableCell className="text-sm font-medium">{c.client_name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground tabular-nums">{formatDate(c.created_at)}</TableCell>
                  {canManage && (
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`client-menu-${c.client_code}`}><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => startEdit(c)}><PencilLine className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openHistory(c)}><History className="mr-2 h-4 w-4" /> History</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-[hsl(var(--danger))] focus:text-[hsl(var(--danger))]" onClick={() => remove(c)}>
                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={canManage ? 4 : 3} className="py-10 text-center text-sm text-muted-foreground">No clients yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="client-edit-dialog">
          <DialogHeader><DialogTitle>Edit client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Client Code</Label>
              <Input data-testid="client-edit-code" value={editForm.client_code} onChange={(e) => setEditForm({ ...editForm, client_code: e.target.value.toUpperCase() })} className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label>Client Name</Label>
              <Input data-testid="client-edit-name" value={editForm.client_name} onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })} />
            </div>
            <p className="text-[11px] text-muted-foreground">Changes are recorded in the Audit Log. Renaming code cascades to submissions.</p>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="client-edit-save">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!history} onOpenChange={(v) => !v && setHistory(null)}>
        <DialogContent className="max-w-3xl" data-testid="client-history-dialog">
          <DialogHeader>
            <DialogTitle>
              {history?.client?.client_name} <span className="ml-2 font-mono text-sm text-muted-foreground">{history?.client?.client_code}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead className="label-caps">Date</TableHead>
                  <TableHead className="label-caps">Executive</TableHead>
                  <TableHead className="label-caps">Type</TableHead>
                  <TableHead className="label-caps text-right">Deposit</TableHead>
                  <TableHead className="label-caps text-right">Accounts</TableHead>
                  <TableHead className="label-caps">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(history?.submissions || []).map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="text-sm tabular-nums">{formatDate(s.date)}</TableCell>
                    <TableCell className="text-sm">{s.user_name || "—"}</TableCell>
                    <TableCell><StatusPill status={s.client_type}>{s.client_type}</StatusPill></TableCell>
                    <TableCell className="text-right tabular-nums">{formatCompactINR(s.deposit_amount)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatNumber(s.accounts_opened)}</TableCell>
                    <TableCell><StatusPill status={s.status} /></TableCell>
                  </TableRow>
                ))}
                {(history?.submissions || []).length === 0 && (
                  <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">No submissions for this client yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
